import { useState, useEffect, useCallback } from "react";
import { getFcmToken } from "helpers/firebase";
import { userService } from "src/services";

const DISMISS_KEY = "push_prompt_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 3;

const checkPushSupport = (): boolean => {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  return true;
};

const isDismissedRecently = (): boolean => {
  if (typeof window === "undefined") return false;
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;

  const dismissedDate = new Date(dismissedAt);
  const now = new Date();
  const diffMs = now.getTime() - dismissedDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays < DISMISS_COOLDOWN_DAYS;
};

export const usePushNotification = () => {
  const [isPushSupported, setIsPushSupported] = useState(false);
  const [permissionState, setPermissionState] =
    useState<NotificationPermission>("default");
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    const supported = checkPushSupport();
    setIsPushSupported(supported);

    if (supported) {
      setPermissionState(Notification.permission);
      setIsDismissed(isDismissedRecently());
    }
  }, []);

  const shouldShowPrompt =
    isPushSupported && permissionState === "default" && !isDismissed;

  const enableNotifications = useCallback(async (): Promise<boolean> => {
    if (!isPushSupported) return false;

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      setIsEnabling(true);

      if (permission !== "granted") {
        console.warn("Push notification permission denied by user.");
        return false;
      }

      const fcmToken = await getFcmToken();

      if (fcmToken) {
        console.log("✅ FCM Token obtained:", fcmToken);
        localStorage.setItem("fcm_token", fcmToken);

        await userService.syncFcmToken(fcmToken);
        console.log("✅ FCM token synced to server.");
      } else {
        console.warn("FCM token was null after permission grant.");
      }

      localStorage.removeItem(DISMISS_KEY);

      return true;
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      return false;
    } finally {
      setIsEnabling(false);
    }
  }, [isPushSupported]);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setIsDismissed(true);
  }, []);

  return {
    isPushSupported,
    permissionState,
    shouldShowPrompt,
    isEnabling,
    enableNotifications,
    dismissPrompt,
  };
};
